/** @format */

import { createSlice } from "@reduxjs/toolkit";
import {
    ICollectAccess,
    ICollectSubscription,
} from "../../utility/types/collectTypes";
import {
    fetchCollectSubscription,
    createCollectSubscription,
    resubscribeCollectSubscription,
    cancelCollectSubscription,
    reactivateCollectSubscription,
    updateCollectPaymentMethod,
} from "../actions/collectBillingAction";
import {
    enterImpersonation,
    exitImpersonation,
    setActiveBusiness,
} from "./collectSlice";

// ─────────────────────────────────────────────────────────────────────────
// Collect billing slice (Story 2).
//
// `subscription` is the single source of truth for the current business's
// billing state. `isLoading` covers the initial fetch; `isSubmitting` covers
// mutating actions (create/resubscribe/cancel/reactivate/update-PM) so the
// checkout spinner never clobbers the page-load spinner. `requiresAction` +
// `clientSecret` carry the SCA hint returned by create/resubscribe.
// ─────────────────────────────────────────────────────────────────────────

interface CollectBillingState {
    subscription: ICollectSubscription | null;
    /** Authoritative access summary from the backend (Story 5). */
    access: ICollectAccess | null;
    isLoading: boolean;
    error: string | null;
    fetchRequestId: string | null;

    isSubmitting: boolean;
    submitError: string | null;
    submitRequestId: string | null;

    /** True when the last create/resubscribe needs an SCA challenge. */
    requiresAction: boolean;
    clientSecret: string | null;
}

const initialState: CollectBillingState = {
    subscription: null,
    access: null,
    isLoading: false,
    error: null,
    fetchRequestId: null,
    isSubmitting: false,
    submitError: null,
    submitRequestId: null,
    requiresAction: false,
    clientSecret: null,
};

// Status values that grant premium Collect access (mirrors the backend
// ACCESS_GRANTING set + trialing). Used to optimistically refresh the access
// gate after a successful mutation without waiting for a re-fetch.
const ACCESS_GRANTING: ICollectSubscription["status"][] = [
    "trialing",
    "active",
    "pending",
    "past_due",
    "pending_cancellation",
];

/** Optimistically refresh the access summary from a mutated subscription. */
function syncAccessFromSubscription(
    state: CollectBillingState,
    sub: ICollectSubscription | null,
): void {
    if (!sub) return;
    // Never downgrade a super-admin's free access based on a subscription doc.
    if (state.access?.isSuperAdmin) return;
    // Never optimistically lift a grace-expired lapse: while the subscription
    // is still `past_due`, only a confirmed charge success (status flips to
    // `active`) or an authoritative access refetch restores access (AC10). A
    // mutation that leaves it `past_due` must not re-enable gated features.
    if (
        sub.status === "past_due" &&
        state.access?.state === "past_due_expired"
    ) {
        return;
    }
    const hasAccess = ACCESS_GRANTING.includes(sub.status);
    state.access = {
        state: sub.status,
        hasAccess,
        isSuperAdmin: false,
        isTrialing: sub.status === "trialing",
        trialEndsAt: sub.trialEndsAt,
        currentPeriodEnd: sub.currentPeriodEnd,
        reactivationWindowEndsAt: sub.reactivationWindowEndsAt,
        retentionState: sub.retentionState,
        subscriptionStatus: sub.status,
    };
}

const collectBillingSlice = createSlice({
    name: "collectBilling",
    initialState,
    reducers: {
        clearBillingErrors: (state) => {
            state.error = null;
            state.submitError = null;
        },
        clearRequiresAction: (state) => {
            state.requiresAction = false;
            state.clientSecret = null;
        },
        /** Directly set the subscription (e.g. after client-side SCA confirm). */
        setCollectSubscription: (state, action) => {
            state.subscription = action.payload as ICollectSubscription | null;
        },
        /** Directly set the access summary (e.g. optimistic gate update). */
        setCollectAccess: (state, action) => {
            state.access = action.payload as ICollectAccess | null;
        },
        resetCollectBilling: () => initialState,
    },
    extraReducers: (builder) => {
        // ── Fetch ────────────────────────────────────────────────────────
        builder
            .addCase(fetchCollectSubscription.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                state.subscription = null;
                state.access = null;
                state.fetchRequestId = action.meta.requestId;
            })
            .addCase(fetchCollectSubscription.fulfilled, (state, action) => {
                if (state.fetchRequestId !== action.meta.requestId) return;
                state.isLoading = false;
                state.fetchRequestId = null;
                state.subscription = action.payload.subscription;
                state.access = action.payload.access;
            })
            .addCase(fetchCollectSubscription.rejected, (state, action) => {
                if (state.fetchRequestId !== action.meta.requestId) return;
                state.isLoading = false;
                state.fetchRequestId = null;
                state.error =
                    (action.payload as string) || "Failed to load subscription.";
            });

        // ── Create ───────────────────────────────────────────────────────
        builder
            .addCase(createCollectSubscription.pending, (state, action) => {
                state.isSubmitting = true;
                state.submitError = null;
                state.requiresAction = false;
                state.clientSecret = null;
                state.submitRequestId = action.meta.requestId;
            })
            .addCase(createCollectSubscription.fulfilled, (state, action) => {
                if (state.submitRequestId !== action.meta.requestId) return;
                state.isSubmitting = false;
                state.submitRequestId = null;
                state.subscription = action.payload.subscription;
                state.requiresAction = action.payload.requiresAction;
                state.clientSecret = action.payload.clientSecret ?? null;
                syncAccessFromSubscription(state, action.payload.subscription);
            })
            .addCase(createCollectSubscription.rejected, (state, action) => {
                if (state.submitRequestId !== action.meta.requestId) return;
                state.isSubmitting = false;
                state.submitRequestId = null;
                state.submitError =
                    (action.payload as string) || "Failed to start subscription.";
            });

        // ── Resubscribe ──────────────────────────────────────────────────
        builder
            .addCase(resubscribeCollectSubscription.pending, (state, action) => {
                state.isSubmitting = true;
                state.submitError = null;
                state.requiresAction = false;
                state.clientSecret = null;
                state.submitRequestId = action.meta.requestId;
            })
            .addCase(
                resubscribeCollectSubscription.fulfilled,
                (state, action) => {
                    if (state.submitRequestId !== action.meta.requestId) return;
                    state.isSubmitting = false;
                    state.submitRequestId = null;
                    state.subscription = action.payload.subscription;
                    state.requiresAction = action.payload.requiresAction;
                    state.clientSecret = action.payload.clientSecret ?? null;
                    syncAccessFromSubscription(
                        state,
                        action.payload.subscription,
                    );
                },
            )
            .addCase(
                resubscribeCollectSubscription.rejected,
                (state, action) => {
                    if (state.submitRequestId !== action.meta.requestId) return;
                    state.isSubmitting = false;
                    state.submitRequestId = null;
                    state.submitError =
                        (action.payload as string) || "Failed to resubscribe.";
                },
            );

        // ── Cancel ───────────────────────────────────────────────────────
        builder
            .addCase(cancelCollectSubscription.pending, (state, action) => {
                state.isSubmitting = true;
                state.submitError = null;
                state.submitRequestId = action.meta.requestId;
            })
            .addCase(cancelCollectSubscription.fulfilled, (state, action) => {
                if (state.submitRequestId !== action.meta.requestId) return;
                state.isSubmitting = false;
                state.submitRequestId = null;
                state.subscription = action.payload;
                syncAccessFromSubscription(state, action.payload);
            })
            .addCase(cancelCollectSubscription.rejected, (state, action) => {
                if (state.submitRequestId !== action.meta.requestId) return;
                state.isSubmitting = false;
                state.submitRequestId = null;
                state.submitError =
                    (action.payload as string) ||
                    "Failed to cancel subscription.";
            });

        // ── Reactivate ───────────────────────────────────────────────────
        builder
            .addCase(reactivateCollectSubscription.pending, (state, action) => {
                state.isSubmitting = true;
                state.submitError = null;
                state.submitRequestId = action.meta.requestId;
            })
            .addCase(
                reactivateCollectSubscription.fulfilled,
                (state, action) => {
                    if (state.submitRequestId !== action.meta.requestId) return;
                    state.isSubmitting = false;
                    state.submitRequestId = null;
                    state.subscription = action.payload;
                    syncAccessFromSubscription(state, action.payload);
                },
            )
            .addCase(
                reactivateCollectSubscription.rejected,
                (state, action) => {
                    if (state.submitRequestId !== action.meta.requestId) return;
                    state.isSubmitting = false;
                    state.submitRequestId = null;
                    state.submitError =
                        (action.payload as string) ||
                        "Failed to reactivate subscription.";
                },
            );

        // ── Update payment method ────────────────────────────────────────
        builder
            .addCase(updateCollectPaymentMethod.pending, (state, action) => {
                state.isSubmitting = true;
                state.submitError = null;
                state.submitRequestId = action.meta.requestId;
            })
            .addCase(updateCollectPaymentMethod.fulfilled, (state, action) => {
                if (state.submitRequestId !== action.meta.requestId) return;
                state.isSubmitting = false;
                state.submitRequestId = null;
                state.subscription = action.payload;
                syncAccessFromSubscription(state, action.payload);
            })
            .addCase(updateCollectPaymentMethod.rejected, (state, action) => {
                if (state.submitRequestId !== action.meta.requestId) return;
                state.isSubmitting = false;
                state.submitRequestId = null;
                state.submitError =
                    (action.payload as string) ||
                    "Failed to update payment method.";
            })
            .addCase(setActiveBusiness, () => ({ ...initialState }))
            .addCase(enterImpersonation, () => ({ ...initialState }))
            .addCase(exitImpersonation, () => ({ ...initialState }));
    },
});

export const {
    clearBillingErrors,
    clearRequiresAction,
    setCollectSubscription,
    setCollectAccess,
    resetCollectBilling,
} = collectBillingSlice.actions;

export default collectBillingSlice.reducer;
